/**
 * Animation Configuration - THE ONESCENT DIAL
 * Celestial Flow + Orbital Motion Hybrid System
 * 
 * Animations are themed to complement user actions and content types.
 */

// =============================================================================
// SPRING CONFIGURATIONS (Celestial Physics)
// =============================================================================

export const SPRINGS = {
  // Gentle, ethereal - for modals, panels
  celestial: { type: 'spring', stiffness: 100, damping: 20, mass: 1 },
  
  // Smooth flow - for content reveals
  flow: { type: 'spring', stiffness: 150, damping: 25, mass: 0.8 },
  
  // Quick but soft - for buttons, interactions  
  responsive: { type: 'spring', stiffness: 300, damping: 30, mass: 0.5 },
  
  // Orbital motion - for circular/rotating elements
  orbital: { type: 'spring', stiffness: 80, damping: 15, mass: 1.2 },
  
  // Subtle drift - for background elements
  drift: { type: 'spring', stiffness: 50, damping: 20, mass: 1.5 },
};

// =============================================================================
// EASING CURVES (Bezier - No Bounce)
// =============================================================================

export const EASINGS = {
  // Smooth materialize effect
  materialize: [0.23, 1, 0.32, 1],
  
  // Gentle cosmic drift
  drift: [0.25, 0.46, 0.45, 0.94],
  
  // Elegant entrance
  enter: [0.0, 0.0, 0.2, 1],
  
  // Soft exit
  exit: [0.4, 0.0, 1, 1],
  
  // Orbital arc
  arc: [0.68, -0.55, 0.27, 1.55],
};

// =============================================================================
// ANIMATION VARIANTS BY THEME
// =============================================================================

// Modal/Panel animations (Celestial Flow)
export const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.92,
    y: 20,
    filter: 'blur(8px)',
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.4,
      ease: EASINGS.materialize,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: -10,
    filter: 'blur(4px)',
    transition: {
      duration: 0.25,
      ease: EASINGS.exit,
    }
  }
};

// Backdrop fade
export const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3, ease: EASINGS.drift }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Content reveal (staggered children)
export const contentRevealVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    }
  }
};

export const contentItemVariants = {
  hidden: { 
    opacity: 0, 
    y: 15,
    filter: 'blur(4px)',
  },
  visible: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.35,
      ease: EASINGS.materialize,
    }
  }
};

// Orbital entry (for cosmic/planetary elements)
export const orbitalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    rotate: -15,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: EASINGS.arc,
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    rotate: 10,
    transition: { duration: 0.3 }
  }
};

// Button/Interactive hover states
export const buttonVariants = {
  idle: { scale: 1 },
  hover: { 
    scale: 1.02,
    transition: { duration: 0.2, ease: EASINGS.enter }
  },
  tap: { 
    scale: 0.98,
    transition: { duration: 0.1 }
  }
};

// Tab switching
export const tabContentVariants = {
  hidden: { 
    opacity: 0,
    x: 20,
  },
  visible: { 
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: EASINGS.materialize,
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2 }
  }
};

// Notification/Toast animations
export const toastVariants = {
  hidden: { 
    opacity: 0, 
    y: -20,
    scale: 0.9,
    filter: 'blur(4px)',
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20,
    }
  },
  exit: { 
    opacity: 0, 
    y: -10,
    scale: 0.95,
    transition: { duration: 0.2 }
  }
};

// Floating/Drift animation (for background elements)
export const floatVariants = {
  animate: {
    y: [0, -8, 0],
    transition: {
      duration: 4,
      ease: 'easeInOut',
      repeat: Infinity,
    }
  }
};

// Pulse/Glow animation (for highlights)
export const pulseVariants = {
  animate: {
    opacity: [0.5, 1, 0.5],
    scale: [1, 1.02, 1],
    transition: {
      duration: 2,
      ease: 'easeInOut',
      repeat: Infinity,
    }
  }
};

// Shimmer effect (for loading states)
export const shimmerVariants = {
  animate: {
    backgroundPosition: ['200% 0', '-200% 0'],
    transition: {
      duration: 2,
      ease: 'linear',
      repeat: Infinity,
    }
  }
};

// =============================================================================
// THEME-SPECIFIC ANIMATIONS
// =============================================================================

export const THEME_ANIMATIONS = {
  // Location/Spacio theme - grounded, earthy drift
  spacio: {
    enter: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: EASINGS.drift }
  },
  
  // Temporal/Time theme - flowing, continuous
  temporal: {
    enter: { opacity: 0, x: -30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.4, ease: EASINGS.flow }
  },
  
  // Celestial/Planets theme - orbital, rotating
  celestial: {
    enter: { opacity: 0, scale: 0.85, rotate: -10 },
    animate: { opacity: 1, scale: 1, rotate: 0 },
    transition: { duration: 0.5, ease: EASINGS.arc }
  },
  
  // Weather/Solar theme - radiant, expanding
  solar: {
    enter: { opacity: 0, scale: 0.9, filter: 'blur(10px)' },
    animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
    transition: { duration: 0.4, ease: EASINGS.materialize }
  },
  
  // Synthesis/Integration theme - complex, layered
  synthesis: {
    enter: { opacity: 0, y: 20, rotateX: -15 },
    animate: { opacity: 1, y: 0, rotateX: 0 },
    transition: { duration: 0.5, ease: EASINGS.celestial }
  },
};

// =============================================================================
// INTENSITY LEVELS
// =============================================================================

export const ANIMATION_INTENSITY = {
  off: {
    duration: 0,
    scale: 1,
    blur: false,
  },
  subtle: {
    duration: 0.5, // 50% of normal
    scale: 0.5,    // Reduced movement
    blur: false,
  },
  normal: {
    duration: 1,
    scale: 1,
    blur: true,
  },
  enhanced: {
    duration: 1.2, // Slightly longer
    scale: 1.2,    // More pronounced
    blur: true,
  }
};

// Helper to apply intensity to animation config
export const applyIntensity = (animation, intensity = 'normal') => {
  const config = ANIMATION_INTENSITY[intensity] || ANIMATION_INTENSITY.normal;
  
  if (intensity === 'off') {
    return {
      initial: {},
      animate: {},
      exit: {},
      transition: { duration: 0 }
    };
  }
  
  return {
    ...animation,
    transition: {
      ...animation.transition,
      duration: (animation.transition?.duration || 0.3) * config.duration,
    }
  };
};

export default {
  SPRINGS,
  EASINGS,
  modalVariants,
  backdropVariants,
  contentRevealVariants,
  contentItemVariants,
  orbitalVariants,
  buttonVariants,
  tabContentVariants,
  toastVariants,
  floatVariants,
  pulseVariants,
  shimmerVariants,
  THEME_ANIMATIONS,
  ANIMATION_INTENSITY,
  applyIntensity,
};
